import { Component, InjectionToken, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { User } from '@common/users/models/user.model';
import { cache } from '@common/util/cache.operator';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { LicenseStorageService } from '@portal-core/license-storage/services/license-storage.service';
import { LicenseUserSeatType } from '@portal-core/license-users/enums/license-user-seat-type.enum';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { LicenseUsersService } from '@portal-core/license-users/services/license-users.service';
import { LicenseProfileComponent, LicenseProfileForm } from '@portal-core/licenses/components/license-profile/license-profile.component';
import { LicenseActivationType } from '@portal-core/licenses/enums/license-activation-type.enum';
import { LicenseProfileTab } from '@portal-core/licenses/enums/license-profile-tab.enum';
import { LicenseType } from '@portal-core/licenses/enums/license-type.enum';
import { CentralPermissions } from '@portal-core/permissions/enums/central-permissions.enum';
import { PermissionsService } from '@portal-core/permissions/services/permissions.service';
import { LicenseProfile } from '@portal-core/profiles/models/license-profile.model';
import { ProfilesService } from '@portal-core/profiles/services/profiles.service';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';
import { LoadingState } from '@portal-core/util/loading-state';
import dayjs from 'dayjs';
import { BehaviorSubject, combineLatest, distinctUntilChanged, first, map, Observable, switchMap } from 'rxjs';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NgIf, AsyncPipe } from '@angular/common';
import { LoaderComponent } from '@portal-core/general/components/loader/loader.component';
import { ErrorsExpansionPanelComponent } from '@portal-core/general/components/errors-expansion-panel/errors-expansion-panel.component';

export interface LicenseProfileOptions {
  showSettingsTab?: boolean | (() => boolean);
  showSubscriptionTab?: boolean | (() => boolean);
  showIntegrationsTab?: boolean | (() => boolean);
  showSecurityTab?: boolean | (() => boolean);
  showSsoTab?: boolean | (() => boolean);
}
export const MC_LICENSE_PROFILE_OPTIONS = new InjectionToken<LicenseProfileOptions>('LicenseProfileOptions');

export interface LicenseProfileDialogData {
  licenseId: number;
  licenseUserId: number;
  sysAdminUser?: User;
  licenseProfileTab?: LicenseProfileTab;
  licenseProfileTabGroupIndex?: number;
}

/*
 * Parent component housing and sending data to all setting components.
*/
@Component({
    selector: 'mc-license-profile-dialog',
    templateUrl: './license-profile-dialog.component.html',
    styleUrls: ['./license-profile-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [MatIconButton, MatIcon, LicenseProfileComponent, NgIf, LoaderComponent, ErrorsExpansionPanelComponent, AsyncPipe]
})
export class LicenseProfileDialogComponent extends DialogBase<LicenseProfileDialogComponent, void> implements OnInit {
  public readonly licenseProfileOptions = inject<LicenseProfileOptions>(MC_LICENSE_PROFILE_OPTIONS, { optional: true });
  public readonly data = inject<LicenseProfileDialogData>(MAT_DIALOG_DATA);
  private readonly profilesService = inject(ProfilesService);
  private readonly errorService = inject(ErrorService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly licenseUsersService = inject(LicenseUsersService);
  private readonly licenseStorageService = inject(LicenseStorageService);

  static DialogConfig: MatDialogConfig = {
    width: '96rem',
    height: '94rem'
  };

  LicenseProfileTab: typeof LicenseProfileTab = LicenseProfileTab;
  LicenseType: typeof LicenseType = LicenseType;
  LicenseActivationType: typeof LicenseActivationType = LicenseActivationType;

  licenseId: number;
  licenseProfile$: Observable<LicenseProfile>;
  licenseProfileTab: LicenseProfileTab;
  licenseUser$: Observable<LicenseUser>;
  loadingState: LoadingState<string> = new LoadingState<string>();
  reload$: BehaviorSubject<void> = new BehaviorSubject(undefined);
  expirationDate$: Observable<Date>;
  userIsAuthor$: Observable<boolean>;
  userCanManageServer$: Observable<boolean>;
  userCanManageIntegrations$: Observable<boolean>;

  @ViewChild(LicenseProfileComponent, { static: true }) licenseProfileComponent: LicenseProfileComponent;

  public override get requirePromptOnClose(): boolean {
    return this.licenseProfileComponent?.requirePromptOnClose;
  }

  override ngOnInit() {
    super.ngOnInit();

    this.loadingState.update(true);
    this.licenseId = this.data.licenseId;

    // Create an observable of the license profile. Start with reload$ so that the data can be reloaded as needed
    this.licenseProfile$ = this.reload$.pipe(
      switchMap(() => this.profilesService.getLicenseProfile$(this.licenseId)),
      cache()
    );

    // Create an observable for the license user
    this.licenseUser$ = this.data.sysAdminUser ?
      // sys admin request
      this.licenseUsersService.buildSysAdminLicenseUser$(this.data.sysAdminUser, this.licenseId)
      // else user request
      : this.licenseUsersService.getItemById$(this.data.licenseUserId, { forceApiRequest: true });

    // Create observables for the user's permissions
    this.userCanManageServer$ = this.permissionsService.licenseUserHasPermission$(this.licenseUser$, CentralPermissions.ServerManagement);
    this.userCanManageIntegrations$ = this.permissionsService.licenseUserHasPermission$(this.licenseUser$, CentralPermissions.ManageIntegrations);

    // Create an observable for the license expiration date
    this.expirationDate$ = this.licenseStorageService.getItemById$(this.licenseId).pipe(
      map(licenseStorage => {
        if (licenseStorage) {
          // If not date specified, assume it expires in a month
          return licenseStorage.CentralEndDate ? licenseStorage.CentralEndDate : dayjs(licenseStorage.CentralStartDate).add(1, 'month').toDate();
        } else {
          return null;
        }
      })
    );

    // Create an observable for the license user's seat type
    this.userIsAuthor$ = this.licenseUser$.pipe(
      map(licenseUser => licenseUser && licenseUser.SeatType === LicenseUserSeatType.Author),
      distinctUntilChanged()
    );

    // The dialog is done loading after the license user, license, and permissions have loaded
    combineLatest([this.userCanManageServer$, this.userCanManageIntegrations$, this.licenseProfile$, this.licenseUser$, this.expirationDate$]).pipe(
      first()
    ).subscribe(([userCanManageServer, userCanManageIntegrations]) => {
      this.setLicenseTab(this.data.licenseProfileTab, userCanManageServer, userCanManageIntegrations);
      this.loadingState.update(false);
      this.onLoaded();
    }, error => {
      this.loadingState.update(false, 'Unable to load the license\'s profile.', this.errorService.getErrorMessages(error))
    });
  }

  onSecuritySaved() {
    this.reload();
  }

  onSettingsSaved() {
    this.reload();
  }

  onSsoSettingsSaved() {
    this.reload();
  }

  onSaved(licenseProfileForm: LicenseProfileForm) {
    switch (licenseProfileForm) {
      case LicenseProfileForm.Settings:
      case LicenseProfileForm.Security:
      case LicenseProfileForm.Sso:
        this.reload();
        break;
    }
  }

  private reload() {
    this.reload$.next();
  }

  onCancel() {
    this.closeDialog();
  }

  private setLicenseTab(preferredLicenseProfileTab: LicenseProfileTab, userCanManageServer: boolean, userCanManageIntegrations: boolean) {
    if (preferredLicenseProfileTab === LicenseProfileTab.Subscription) {
      this.licenseProfileTab = preferredLicenseProfileTab;
    } else if ((preferredLicenseProfileTab === LicenseProfileTab.Security || preferredLicenseProfileTab === LicenseProfileTab.Settings || preferredLicenseProfileTab === LicenseProfileTab.SSO) && userCanManageServer) {
      this.licenseProfileTab = preferredLicenseProfileTab;
    } else if (preferredLicenseProfileTab === LicenseProfileTab.Integrations && userCanManageIntegrations) {
      this.licenseProfileTab = preferredLicenseProfileTab;
    } else {
      this.licenseProfileTab = LicenseProfileTab.Overview;
    }
  }
}
